import type { ParsedQuestionBlock, ParsedQuestionnaire, QuestionnaireProgress } from "./mcp-chat-types";
import { isOtherSelection } from "./mcp-chat-utils";

/**
 * Is a single clarifying-question answered?
 *
 * Multi-select answers live in `multiSelectedValues` (a non-empty array), NOT in
 * `selectedValues`. Single-select answers live in `selectedValues`, and a
 * freeform "Other" pick additionally needs a non-empty `customValues` entry.
 *
 * This predicate is centralized ON PURPOSE: it used to be inlined at four call
 * sites (per-question + all-answered gate, in both the inline-questionnaire and
 * the interrupt-question render paths). One copy forgot the `multiSelect` branch
 * and only read `selectedValues`, so a multi-select question could never satisfy
 * the gate and "Submit answers" stayed disabled forever no matter how many
 * options were ticked. Keep all call sites on this one function.
 */
export function isQuestionAnswered(
  question: ParsedQuestionBlock,
  index: number,
  progress: QuestionnaireProgress
): boolean {
  if (question.multiSelect) {
    return (progress.multiSelectedValues?.[index] || []).length > 0;
  }
  const selected: string = progress.selectedValues[index] || "";
  if (!selected) return false;
  if (isOtherSelection(question, selected)) {
    return (progress.customValues[index] || "").trim().length > 0;
  }
  return true;
}

/** Every question is answered — the gate for the "Submit answers" button. */
export function areAllQuestionsAnswered(questionnaire: ParsedQuestionnaire, progress: QuestionnaireProgress): boolean {
  return (
    questionnaire.questions.length > 0 &&
    questionnaire.questions.every((q: ParsedQuestionBlock, i: number) => isQuestionAnswered(q, i, progress))
  );
}

/** The user pressed "You decide this one" on this question. */
export function isQuestionDelegated(index: number, progress: QuestionnaireProgress): boolean {
  return (progress.delegatedIndices ?? []).includes(index);
}

/**
 * This question is settled — answered, or explicitly handed to the analyst.
 *
 * "Settled" is what the wizard should gate forward movement on, NOT answeredness.
 * Gating Next on answeredness alone stranded the user: step back to a question you
 * skipped and Next stayed dead, so the only way onward was to pick an option
 * (un-skipping it) and then skip it again.
 */
export function isQuestionResolved(
  question: ParsedQuestionBlock,
  index: number,
  progress: QuestionnaireProgress
): boolean {
  return isQuestionAnswered(question, index, progress) || isQuestionDelegated(index, progress);
}

/**
 * Every question is either answered or explicitly handed back — the gate for the
 * "Submit answers" button on a card that offers delegation.
 *
 * Deliberately a SIBLING of `areAllQuestionsAnswered` rather than a widening of
 * it. The markdown-parsed questionnaire path has no delegate affordance, so it
 * keeps the strict rule: an untouched question must still block Submit there, and
 * here too. Only a deliberate hand-back counts, so nothing becomes submittable by
 * accident.
 */
export function areAllQuestionsResolved(questionnaire: ParsedQuestionnaire, progress: QuestionnaireProgress): boolean {
  return (
    questionnaire.questions.length > 0 &&
    questionnaire.questions.every((q: ParsedQuestionBlock, i: number) => isQuestionResolved(q, i, progress))
  );
}

/**
 * Drop question `index` from the skipped set, because the user just answered it.
 *
 * Answering and handing back are mutually exclusive. While the skipped state was
 * invisible this staleness was harmless — every render path checks answeredness
 * first — but the step now renders a grey dash, so a leftover mark would tell the
 * user the analyst decided something they chose themselves.
 */
export function clearSkippedMark(prev: QuestionnaireProgress, index: number): number[] | undefined {
  const marks: number[] | undefined = prev.delegatedIndices;
  if (marks === undefined || !marks.includes(index)) return marks;
  return marks.filter((i: number): boolean => i !== index);
}
